import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Hero from "./components/Hero";
import Categories from "./components/Categories";
import ProductShowcase from "./components/ProductShowcase";
import WhatIs from "./components/WhatIs";
import Guides from "./components/Guides";
import WhyUs from "./components/WhyUs";
import Articles from "./components/Articles";
import FAQ from "./components/FAQ";
import Safety from "./components/Safety";
import FinalCTA from "./components/FinalCTA";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useSeo, SITE_NAME, SITE_URL, SITE_LOGO } from "@/lib/seo";
import { useJsonLd } from "@/lib/schema";

const homeMeta: Record<string, { title: string; description: string; keywords: string }> = {
  en: {
    title: "CSSBuy Spreadsheet - Resource Center & Buying Guide",
    description: "Explore CSSBuy Spreadsheet resources, products, sellers, QC information, shipping guides and practical buying resources in one organized hub.",
    keywords: "CSSBuy Spreadsheet, CSSBuy products, CSSBuy sellers, CSSBuy shipping, CSSBuy guide, CSSBuy review, CSSBuy QC",
  },
  pt: {
    title: "CSSBuy Spreadsheet - Centro de Recursos e Guias de Compra",
    description: "Explore o CSSBuy Spreadsheet para encontrar produtos, vendedores, informações de QC, guias de envio e dicas práticas em um só hub organizado.",
    keywords: "CSSBuy Spreadsheet, produtos CSSBuy, vendedores CSSBuy, envio CSSBuy, guia CSSBuy, avaliação CSSBuy, QC CSSBuy",
  },
  de: {
    title: "CSSBuy Spreadsheet - Ressourcen Center und Einkaufsratgeber",
    description: "Entdecke CSSBuy Spreadsheet-Ressourcen, Produkte, Verkäufer, QC-Informationen, Versandratgeber und praktische Einkaufstipps an einem Ort.",
    keywords: "CSSBuy Spreadsheet, CSSBuy Produkte, CSSBuy Verkäufer, CSSBuy Versand, CSSBuy Ratgeber, CSSBuy Review, CSSBuy QC",
  },
};

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const pathLang = location.pathname.split("/")[1];
    if (["en", "pt", "de"].includes(pathLang) && i18n.language !== pathLang) {
      i18n.changeLanguage(pathLang);
    }
    document.documentElement.lang = i18n.language;
  }, [location.pathname, i18n]);

  const seoData = useMemo(() => {
    const meta = homeMeta[i18n.language] || homeMeta.en;
    return {
      title: meta.title,
      description: meta.description,
      keywords: meta.keywords,
      image: SITE_LOGO,
      type: "website",
    };
  }, [i18n.language]);

  useSeo(seoData);

  const faqItems = t("home.faq.items", { returnObjects: true }) as Array<{ q: string; a: string }>;

  useJsonLd("home-website", {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    description: seoData.description,
    inLanguage: i18n.language,
  });

  useJsonLd("home-organization", {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: SITE_LOGO,
  });

  useJsonLd("home-faq", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">
        <Hero />
        <Categories />
        <ProductShowcase />
        <WhatIs />
        <Guides />
        <WhyUs />
        <Articles />
        <FAQ />
        <Safety />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}